"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import { useUserActions } from "@/lib/userActions/UserActionsContext";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * LcPlacementFeeCta — fullscreen overlay shown on T2 4.12 before the
 * compliance gate cascade. The presenter clicks "Pay & submit" to
 * dismiss; the click fires `actionKey` so the CTA persists as gone on
 * Shift+N replay (handled by UserActionsContext).
 *
 * Mirrors the T2 4.5 sign-CTA pattern (gate + persisted user action) so
 * the audience reads the LC placement fee as a real bank-mandated step,
 * not a free progression.
 */
export function LcPlacementFeeCta({
  feeUsd,
  bankBadge,
  actionKey,
}: {
  feeUsd: number;
  bankBadge: string;
  actionKey: string;
}) {
  const t = useTranslations("lcPlacementFeeCta");
  const userActions = useUserActions();
  const already = userActions.has(actionKey);
  const [open, setOpen] = useState(!already);
  if (!open) return null;
  if (typeof document === "undefined") return null;

  // Portal: escape .scene-fade so the CTA centres on the viewport
  // (the inline z-30 overlay was being constrained by the SceneRouter
  // wrapper's transform-induced containing block).
  return createPortal(
    <div className="fixed inset-0 z-30 grid place-items-center bg-orbitra-ink/55 backdrop-blur-sm">
      <div className="glass w-[min(440px,90vw)] p-6 text-center">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
          {t("kicker")}
        </p>
        <h3 className="mt-2 font-display text-base font-semibold text-orbitra-paper">
          {t("heading")}
        </h3>
        <div className="mt-3 rounded-md border border-state-financed/30 bg-state-financed/10 px-4 py-3">
          <p className="font-display text-2xl font-semibold text-orbitra-paper">
            USD {feeUsd.toLocaleString()}
          </p>
        </div>
        <p className="mt-3 text-[11px] text-orbitra-muted">
          {t("body")}
        </p>
        <button
          type="button"
          autoFocus
          onClick={() => {
            userActions.fire(actionKey);
            setOpen(false);
          }}
          className="cta-primary mt-4 w-full px-5 py-2 font-display text-sm font-semibold"
        >
          {t("ctaButton")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
